import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId } from "react";

export type Branding = { app_name: string; logo_url: string; tagline: string };
export type WalletSettings = {
  min_add: number; max_add: number; min_withdraw: number; max_withdraw: number;
  upi_id: string; upi_qr_url: string;
};
export type ContactSettings = {
  whatsapp: string; telegram: string; customer_care: string; website: string;
};
export type ReferralActivationRule = "signup" | "first_verified_deposit" | "first_valid_bet" | "deposit_and_play";
export type ReferralSettings = {
  bonus: number;
  min_play: number;
  description: string;
  activation_rule: ReferralActivationRule;
  min_verified_deposit: number;
  enabled: boolean;
};
export type SystemSettings = { maintenance: boolean; maintenance_message: string };
export type BettingSettings = {
  min_bet: number; max_bet: number; max_numbers: number; multiplier: number;
};
export type NoticesSettings = { deposit: string; withdraw: string };
export type HomeSettings = { banner_url: string };
export type AppUpdateSettings = {
  current_app_version: string;
  minimum_required_version: string;
  current_version_code: number;
  minimum_required_version_code: number;
  latest_apk_download_link: string;
  force_update_enabled: boolean;
  update_message: string;
};
export type TelegramSettings = { admin_username: string; channel_username: string };
export type PaymentContactSettings = {
  telegram_link: string;
  whatsapp_number: string;
  telegram_enabled: boolean;
  whatsapp_enabled: boolean;
};
export type WithdrawSettings = {
  enabled: boolean;
  open_time: string; // HH:MM (Asia/Kolkata)
  close_time: string; // HH:MM (Asia/Kolkata)
  closed_message: string;
};

/** Manual = existing Telegram/WhatsApp flow. Automatic = IMB UPI Gateway. */
export type DepositMode = "manual" | "automatic";
export type DepositModeSettings = { mode: DepositMode };
export type ImbGatewaySettings = {
  enabled: boolean;
  environment: "test" | "live";
  min_amount: number;
  max_amount: number;
  timeout_minutes: number;
  auto_recheck: boolean;
  manual_fallback: boolean;
  maintenance_message: string;
};


// Per-number (00-99) daily bet-amount ceiling; keys are stringified numbers.
// Missing key or non-positive value => no limit for that number.
export type NumberLimits = Record<string, number>;




const DEFAULT_WALLET: WalletSettings = {
  min_add: 100, max_add: 50000, min_withdraw: 200, max_withdraw: 25000,
  upi_id: "brothersclub@upi", upi_qr_url: "",
};
const DEFAULT_BRANDING: Branding = { app_name: "Brothers Club", logo_url: "", tagline: "" };
const DEFAULT_CONTACT: ContactSettings = { whatsapp: "", telegram: "", customer_care: "", website: "" };
const DEFAULT_REFERRAL: ReferralSettings = { bonus: 50, min_play: 100, description: "", activation_rule: "first_verified_deposit", min_verified_deposit: 100, enabled: true };
const DEFAULT_SYSTEM: SystemSettings = { maintenance: false, maintenance_message: "App is under maintenance. Please try later." };
const DEFAULT_BETTING: BettingSettings = { min_bet: 10, max_bet: 10000, max_numbers: 50, multiplier: 100 };
const DEFAULT_NOTICES: NoticesSettings = { deposit: "", withdraw: "" };
const DEFAULT_HOME: HomeSettings = { banner_url: "" };
const DEFAULT_APP_UPDATE: AppUpdateSettings = {
  current_app_version: "1.0.0",
  minimum_required_version: "",
  current_version_code: 0,
  minimum_required_version_code: 0,
  latest_apk_download_link: "",
  force_update_enabled: false,
  update_message: "New update available. Please update the app to continue.",
};
const DEFAULT_TELEGRAM: TelegramSettings = { admin_username: "", channel_username: "" };
const DEFAULT_PAYMENT_CONTACT: PaymentContactSettings = {
  telegram_link: "",
  whatsapp_number: "",
  telegram_enabled: false,
  whatsapp_enabled: false,
};
const DEFAULT_WITHDRAW: WithdrawSettings = {
  enabled: true,
  open_time: "10:00",
  close_time: "22:00",
  closed_message: "Withdrawals are currently closed. Please try again during the allowed time window.",
};
const DEFAULT_NUMBER_LIMITS: NumberLimits = {};
const DEFAULT_DEPOSIT_MODE: DepositModeSettings = { mode: "manual" };
const DEFAULT_IMB_GATEWAY: ImbGatewaySettings = {
  enabled: false,
  environment: "test",
  min_amount: 0,
  max_amount: 0,
  timeout_minutes: 15,
  auto_recheck: true,
  manual_fallback: true,
  maintenance_message: "Automatic payments are temporarily unavailable. Please try again later.",
};




export const DEFAULT_APP_SETTINGS = {
  branding: DEFAULT_BRANDING,
  wallet: DEFAULT_WALLET,
  contact: DEFAULT_CONTACT,
  referral: DEFAULT_REFERRAL,
  system: DEFAULT_SYSTEM,
  betting: DEFAULT_BETTING,
  notices: DEFAULT_NOTICES,
  home: DEFAULT_HOME,
  app_update: DEFAULT_APP_UPDATE,
  telegram: DEFAULT_TELEGRAM,
  payment_contact: DEFAULT_PAYMENT_CONTACT,
  withdraw: DEFAULT_WITHDRAW,
  number_limits: DEFAULT_NUMBER_LIMITS as NumberLimits,
  deposit_mode: DEFAULT_DEPOSIT_MODE,
  imb_gateway: DEFAULT_IMB_GATEWAY,

};


export function useAppSettings() {
  const qc = useQueryClient();
  const channelName = `app_settings_changes_${useId().replace(/:/g, "_")}`;
  useEffect(() => {
    let ch: ReturnType<typeof supabase.channel> | null = null;
    try {
      ch = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "app_settings" },
          () => {
            try { qc.invalidateQueries({ queryKey: ["app_settings"] }); } catch (e) { console.warn("settings invalidate failed", e); }
          }
        )
        .subscribe((_status, err) => { if (err) console.warn("app_settings realtime error", err); });
    } catch (e) {
      console.warn("app_settings realtime subscribe failed", e);
    }
    return () => {
      try { if (ch) supabase.removeChannel(ch); } catch (e) { console.warn("removeChannel failed", e); }
    };
  }, [channelName, qc]);
  const query = useQuery({
    queryKey: ["app_settings"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.from("app_settings").select("key,value");
        if (error) throw error;
        const map: Record<string, any> = {};
        for (const row of data ?? []) map[row.key] = row.value;
        return {
          branding: { ...DEFAULT_BRANDING, ...(map.branding ?? {}) } as Branding,
          wallet: { ...DEFAULT_WALLET, ...(map.wallet ?? {}) } as WalletSettings,
          contact: { ...DEFAULT_CONTACT, ...(map.contact ?? {}) } as ContactSettings,
          referral: { ...DEFAULT_REFERRAL, ...(map.referral ?? {}) } as ReferralSettings,
          system: { ...DEFAULT_SYSTEM, ...(map.system ?? {}) } as SystemSettings,
          betting: { ...DEFAULT_BETTING, ...(map.betting ?? {}) } as BettingSettings,
          notices: { ...DEFAULT_NOTICES, ...(map.notices ?? {}) } as NoticesSettings,
          home: { ...DEFAULT_HOME, ...(map.home ?? {}) } as HomeSettings,
          app_update: { ...DEFAULT_APP_UPDATE, ...(map.app_update ?? {}) } as AppUpdateSettings,
          telegram: { ...DEFAULT_TELEGRAM, ...(map.telegram ?? {}) } as TelegramSettings,
          payment_contact: { ...DEFAULT_PAYMENT_CONTACT, ...(map.payment_contact ?? {}) } as PaymentContactSettings,
          withdraw: { ...DEFAULT_WITHDRAW, ...(map.withdraw ?? {}) } as WithdrawSettings,
          number_limits: { ...(map.number_limits ?? {}) } as NumberLimits,
          deposit_mode: { ...DEFAULT_DEPOSIT_MODE, ...(map.deposit_mode ?? {}) } as DepositModeSettings,
          imb_gateway: { ...DEFAULT_IMB_GATEWAY, ...(map.imb_gateway ?? {}) } as ImbGatewaySettings,


        };
      } catch (e) {
        console.warn("app_settings fetch failed, using defaults", e);
        return DEFAULT_APP_SETTINGS;
      }
    },
    staleTime: 5 * 60_000, // realtime invalidates on change; 5 min otherwise
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: DEFAULT_APP_SETTINGS,
  });
  return query;
}

